import { Box } from '@mui/material';
import React, { useCallback, useEffect, useRef } from 'react';

interface InlineTextProps {
  value: string;
  onChange: (value: string) => void;
  /** Hint shown when the field is empty / unset. */
  placeholder: string;
  /**
   * The stored value is a placeholder / not-yet-set marker (decided by the
   * caller via the shared `…NotSet` helpers). Shown as the placeholder hint.
   */
  unset?: boolean;
  ariaLabel: string;
  /** Typography styles so the field reads in its real display style. */
  sx?: React.CSSProperties;
}

/**
 * Single-line, click-to-edit plain text rendered in its real display style.
 * Empty / unset shows a dimmed italic placeholder; hovering tints it to hint it
 * is editable; focusing shows an edit ring. Commits on blur and Enter; Escape
 * reverts. Formatting and line breaks are stripped — this is plain text.
 */
export default function InlineText({
  value,
  onChange,
  placeholder,
  unset,
  ariaLabel,
  sx,
}: InlineTextProps) {
  const ref = useRef<HTMLSpanElement>(null);
  const showPlaceholder = Boolean(unset) || value.trim() === '';
  const display = showPlaceholder ? '' : value;

  // Keep the DOM text in sync with props while the field is not being edited.
  useEffect(() => {
    const el = ref.current;
    if (el && document.activeElement !== el && el.textContent !== display) {
      el.textContent = display;
    }
  }, [display]);

  const commit = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const next = (el.textContent ?? '').replace(/\s+/g, ' ').trim();
    if (next !== (showPlaceholder ? '' : value)) onChange(next);
  }, [onChange, showPlaceholder, value]);

  return (
    <Box
      component="span"
      ref={ref}
      role="textbox"
      aria-label={ariaLabel}
      aria-multiline={false}
      contentEditable
      suppressContentEditableWarning
      data-placeholder={placeholder}
      spellCheck
      onBlur={commit}
      onKeyDown={(event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          (event.target as HTMLElement).blur();
        } else if (event.key === 'Escape') {
          event.preventDefault();
          if (ref.current) ref.current.textContent = display;
          (event.target as HTMLElement).blur();
        }
      }}
      sx={{
        display: 'block',
        outline: 'none',
        borderRadius: 1,
        px: 0.5,
        mx: -0.5,
        cursor: 'text',
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
        transition: 'background-color 0.12s ease, box-shadow 0.12s ease',
        '&:hover': { bgcolor: 'action.hover' },
        '&:focus': {
          bgcolor: 'transparent',
          boxShadow: (theme) => `0 0 0 2px ${theme.palette.primary.main}`,
        },
        '&:empty::before': {
          content: 'attr(data-placeholder)',
          color: 'text.disabled',
          fontStyle: 'italic',
          fontWeight: 400,
        },
        ...sx,
      }}
    />
  );
}
